import React, { useEffect } from 'react'
import { NestDataObject, useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import pclnExperimentation from '@pcln/experimentation'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORIES, INVALID } from '@/constants/analytics'
import { SetValueFunctionType } from '@/types'
import { PackagesFormStateType, MakeWatchObject } from '../types'
import isMoreThanFourPerRoom from '@/shared-utils/packages-room-count'

const HOTEL = 'STAY'

type ChildrenFunctionArgs = {
  setValue: SetValueFunctionType<PackagesFormStateType>
  errors: NestDataObject<PackagesFormStateType>
  // eslint-disable-next-line @typescript-eslint/ban-types
  register: Function
  watchState: MakeWatchObject<PackagesFormStateType>
}

type FormContainerProps = {
  children: (args: ChildrenFunctionArgs) => JSX.Element
  initialState: PackagesFormStateType
  onSubmit: (data: PackagesFormStateType) => void
  searchFormSchema: any
  fields: string[]
  isMoreRoomCapacityAllowed: boolean
}
export default function FormContainer({
  children,
  initialState,
  onSubmit,
  fields,
  searchFormSchema,
  isMoreRoomCapacityAllowed
}: FormContainerProps) {
  const {
    register,
    unregister,
    setValue,
    clearError,
    formState,
    handleSubmit,
    watch,
    errors
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange',
    validationContext: {
      isMoreRoomCapacityAllowed
    }
  })
  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [fields, register, unregister])

  const {
    tripType: watchTripType,
    partialStay: watchPartialStay,
    startLocation: watchStartLocation,
    endLocation: watchEndLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    hotelStartDate: watchHotelStartDate,
    hotelEndDate: watchHotelEndDate,
    pickupCarSomewhere: watchPickupCarSomewhere,
    pickupCarIncludeAirport: watchPickupCarIncludeAirport,
    rcLocation: watchRcLocation,
    rcPickupDate: watchRcPickupDate,
    rcDropoffDate: watchRcDropoffDate,
    rcPickupTime: watchRcPickupTime,
    rcDropoffTime: watchRcDropoffTime,
    gaCategory: watchGaCategory
  } = watch()

  // PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION
  useEffect(() => {
    if (
      watchTripType.includes(HOTEL) &&
      isMoreThanFourPerRoom(
        watchRoomCount,
        watchAdultCount + watchChildrenCount
      )
    ) {
      pclnExperimentation.fireImpression(
        'PKG_FE_SEARCH_FORM_HOTEL_NUM_ADULTS_RESTRICTION'
      )
    }
  }, [watchTripType, watchRoomCount, watchAdultCount, watchChildrenCount])

  useEffect(() => {
    clearError('startLocation')
  }, [clearError, watchStartLocation])
  useEffect(() => {
    clearError('endLocation')
  }, [clearError, watchEndLocation])
  useEffect(() => {
    clearError('rcLocation')
  }, [clearError, watchRcLocation])
  useEffect(() => {
    clearError('startDate')
  }, [clearError, watchStartDate])
  useEffect(() => {
    clearError('endDate')
  }, [clearError, watchEndDate])
  useEffect(() => {
    clearError('hotelStartDate')
  }, [clearError, watchHotelStartDate, watchPartialStay])
  useEffect(() => {
    clearError('hotelEndDate')
  }, [clearError, watchHotelEndDate, watchPartialStay])
  useEffect(() => {
    clearError('hotelStartDateInRange')
  }, [clearError, watchPartialStay, watchStartDate, watchHotelStartDate])
  useEffect(() => {
    clearError('hotelEndDateInRange')
  }, [clearError, watchPartialStay, watchEndDate, watchHotelEndDate])
  useEffect(() => {
    clearError('rcPickupDate')
  }, [clearError, watchRcPickupDate])
  useEffect(() => {
    clearError('rcDropoffDate')
  }, [clearError, watchRcDropoffDate])
  useEffect(() => {
    clearError('isInfantOnInternationalTravel')
  }, [clearError, watchStartLocation, watchEndLocation, watchChildrenAges])
  useEffect(() => {
    clearError('isChildrenAgeProvided')
  }, [clearError, watchChildrenCount, watchChildrenAges])
  useEffect(() => {
    clearError('isRoomCompacityReached')
  }, [
    clearError,
    watchAdultCount,
    watchChildrenCount,
    watchTripType,
    watchRoomCount
  ])
  useEffect(() => {
    if (watchPickupCarSomewhere) clearError('rcDropoffDate')
  }, [clearError, watchPickupCarSomewhere])
  const { pathname } = useRouter()
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  useEffect(() => {
    setValue('gaCategory', ANALYTICS_CATEGORY)
  }, [setValue, ANALYTICS_CATEGORY])

  useEffect(() => {
    if (formState.submitCount && !formState.isValid) {
      analytics.fireEvent({
        category: ANALYTICS_CATEGORY,
        action: 'VP Search Button',
        label: INVALID
      })
    }
  }, [formState.isValid, formState.submitCount, ANALYTICS_CATEGORY])

  return (
    <form aria-label="packages-search-form" onSubmit={handleSubmit(onSubmit)}>
      {children({
        setValue,
        errors,
        register,
        watchState: {
          watchTripType,
          watchAdultCount,
          watchRoomCount,
          watchChildrenAges,
          watchChildrenCount,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchPickupCarSomewhere,
          watchPickupCarIncludeAirport,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime,
          watchGaCategory
        }
      })}
    </form>
  )
}
